import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, map, Observable } from 'rxjs';

import { TranslocoService } from '@ngneat/transloco';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { LocalizeService } from '../pipes/localize.service';
import { ServerInfo } from '../server/serverinfo.model';
import { ServerInfoService } from '../server/serverinfo.service';
import { environment } from '../../../environments/environment';
import { DialogsService } from '../../dialogs/dialogs.service';

interface HttpOptions {
    headers?: HttpHeaders;
    params?: any;
}

@Injectable({ providedIn: 'root' })
export class HttpService {

    // Set static headers (cached headers with a value that will never change)
    private defaultHttpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Cloudcodice-Application': environment.applicationId,
            'X-Cloudcodice-Timezoneoffset': String(new Date().getTimezoneOffset()),
            'X-Cloudcodice-Timezoneid': Intl.DateTimeFormat().resolvedOptions().timeZone + ''
        }),
    };

    private serverInfo: BehaviorSubject<ServerInfo> = this.serverInfoService.getServerInfoObservable();

    private baseUrl = environment.dist ? '' : `${environment.BASE_URL}`;
    private authBaseUrl = `${environment.AUTH_BASE_URL}/json`;

    constructor(
        private http: HttpClient,
        private serverInfoService: ServerInfoService,
        private transloco: TranslocoService,
        private dialogsService: DialogsService,
        private localize: LocalizeService
    ) {
        if (environment.dist) {
            this.serverInfo.subscribe(serverInfo => {
                if (serverInfo) {
                    this.authBaseUrl = `${serverInfo.authServerURL}/json`;
                }
            });
        }
    }

    buildHttpOptions(): HttpOptionsBuilder {
        // Set dynamic headers (headers with a value that cannot be determined from the start, like locale which the user can change at any time)
        return new HttpOptionsBuilder({...this.defaultHttpOptions}).set('X-Cloudcodice-Language', this.transloco.getActiveLang() ?? this.transloco.getDefaultLang());
    }

    get<T = any>(url: string, options?: HttpOptions): Observable<T> {
        const params = options && options.params ? options.params : null;

        const authToken = localStorage.getItem(StorageKeysEnum.accessToken);
        const httpOptions = this.buildHttpOptions().set('Authorization', `Bearer ${authToken}`).build();
        if (params) {
            httpOptions.params = params;
        }
        return this.http.get<T>(`${this.baseUrl}/${url}`, httpOptions).pipe(
            map((response) => {
                this.openEchoDialog(response);
                return response;
            })
        );
    }

    post<T = any>(url: string, body: any, responseType?: 'arraybuffer' | 'blob' | 'json' | 'text'): Observable<T> {
        const authToken = localStorage.getItem(StorageKeysEnum.accessToken);
        const httpOptions = this.buildHttpOptions().set('Authorization', `Bearer ${authToken}`).build();
        if (responseType) {
            httpOptions['responseType'] = responseType;
        }
        return this.http.post<T>(`${this.baseUrl}/json/${url}`, body, httpOptions).pipe(
            map((response) => {
                this.openEchoDialog(response);
                return response;
            })
        );
    }

    postWithHeaders<T = any>(url: string, body: any, responseType?: 'arraybuffer' | 'blob' | 'json' | 'text'): Observable<T> {
        const authToken = localStorage.getItem(StorageKeysEnum.accessToken);
        const httpOptions = this.buildHttpOptions().set('Authorization', `Bearer ${authToken}`).build();
        if (responseType) {
            httpOptions['responseType'] = responseType;
        }
        httpOptions['observe'] = 'response';
        return this.http.post<T>(`${this.baseUrl}/json/${url}`, body, httpOptions).pipe(
            map((response) => {
                this.openEchoDialog(response);
                return response;
            })
        );
    }

    authPost<T = any>(url: string, body: any): Observable<T> {
        return this.http.post<T>(`${this.authBaseUrl}/${url}`, body);
    }

    getBaseUrl() {
        return this.baseUrl;
    }

    private openEchoDialog (response: any) {
        const echo = (response as any)?.echo;
        if (echo && Object.keys(echo).length > 0) {
            if ((response as any)?.status >= 400) {
                this.dialogsService.openErrorDialog({
                    message: this.localize.transform(echo)
                });
                return response;
            } else {
                this.dialogsService.openInfoDialog({
                    message: this.localize.transform(echo)
                });
            }
        }
    }
}

export class HttpOptionsBuilder {

    httpOptions: HttpOptions = { headers: new HttpHeaders() };

    constructor(defaultHttpOptions?: HttpOptions) {
        if (defaultHttpOptions) {
            this.httpOptions = defaultHttpOptions;
        }
    }

    set(name: string, value: string): HttpOptionsBuilder {
        if (value) {
            this.httpOptions.headers = this.httpOptions.headers.set(name, value);
        }
        return this;
    }

    public build(): HttpOptions {
        return this.httpOptions;
    }

}

export function checkOk(ok: {} | HttpErrorResponse): boolean {
    if (!ok) {
        return false;
    }
    if ('status' in ok) {
        return ok.status >= 200 && ok.status < 400;
    }
    return true;
}
